import React, {useState} from 'react';
import DashboardLayout from "../../../layouts/DashboardLayout";
import MutationStatusMessage from "../../../components/MutationStatusMessage";
import PageHeader from "../../../components/PageHeader";
import TextInput from "../../../components/TextInput";
import {useMutation, useQueryClient} from "react-query";
import {apollo} from "../../../utils/apollo";
import {Navigate, useNavigate} from "react-router-dom";
import {CREATE_SUB_VENDOR} from "../../../utils/graphql";
import Toggle from "../../../components/Toggle";
import Button from "../../../components/Button";
import {toast} from "react-toastify";
import {UserIcon, BanknotesIcon, BuildingLibraryIcon} from '@heroicons/react/24/outline';
import {useTranslation} from "react-i18next";
import useAuthStore from "../../../store/auth";

function SubVendorCreate() {
    const {t} = useTranslation();
    const {merchant, roles, permissions} = useAuthStore();

    const [name, setName] = useState('');
    const [businessName, setBusinessName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [sharePercentage, setSharePercentage] = useState('20');
    const [displaySupplierDetails, setDisplaySupplierDetails] = useState(true);
    const [bankAccountHolderName, setBankAccountHolderName] = useState('');
    const [bankAccount, setBankAccount] = useState('');
    const [iban, setIban] = useState('');
    const queryClient = useQueryClient()
    const navigate = useNavigate();

    const createSubVendorMutation = useMutation({
        mutationFn: async () => {
            const {data} = await apollo.mutate({
                mutation: CREATE_SUB_VENDOR, variables: {
                    input: {
                        name,
                        businessName,
                        email,
                        phone,
                        displaySupplierDetails,
                        sharePercentage,
                        bank_account_holder_name: bankAccountHolderName,
                        bank_account: bankAccount,
                        iban,
                    }
                },
            });
            return data?.createSubVendor;
        }, onSuccess: () => {
            queryClient.invalidateQueries(['suppliers_list']).then(() => {
                setTimeout(() => navigate("/merchant/sub-vendors"), 2000);
            });
        },
    })

    const hasAccess = roles?.includes("merchant") || permissions?.includes("edit_suppliers");
    if (!hasAccess || merchant?.allowSubVendors === false) {
        return <Navigate to={'/dashboard'} replace />
    }

    const handleSubVendorForm = () => {
        if (!name) {
            toast.error(t('sub_vendors.create.validation.name_required'))
            return
        }
        if (!businessName) {
            toast.error(t('sub_vendors.create.validation.business_name_required'))
            return
        }
        if (!email) {
            toast.error(t('sub_vendors.create.validation.email_required'))
            return
        }
        if (!phone) {
            toast.error(t('sub_vendors.create.validation.phone_required'))
            return
        }
        if (!sharePercentage || isNaN(Number(sharePercentage)) || Number(sharePercentage) < 1 || Number(sharePercentage) > 99) {
            toast.error(t('sub_vendors.create.validation.share_percentage_invalid'));
            return;
        }
        createSubVendorMutation.mutate()
    }

    return (
        <DashboardLayout>
            <div className="mb-10">
                <div className="flex items-center gap-4 mb-2">
                    <div className="p-3 rounded-2xl bg-white shadow-sm border border-gray-100 text-[#0b6a8c]">
                        <UserIcon className="h-7 w-7" />
                    </div>
                    <div>
                        <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">{t('sub_vendors.create.page_title')}</h2>
                        <p className="text-gray-500 font-medium">{t('sub_vendors.create.page_description')}</p>
                    </div>
                </div>
            </div>

            <MutationStatusMessage mutation={createSubVendorMutation}/>

            <div className="bg-white/60 backdrop-blur-md rounded-[2.5rem] shadow-sm border border-gray-100 mb-8 overflow-hidden">
                <div className="px-8 py-5 border-b border-gray-100/50">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-2xl bg-cyan-50 text-[#0b6a8c]">
                            <UserIcon className="h-5 w-5" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">{t('sub_vendors.create.basic_information')}</h3>
                    </div>
                </div>
                <div className="p-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-50 hover:shadow-md transition-all">
                            <TextInput 
                                name="name" 
                                label={t('sub_vendors.table.supplierName')} 
                                id="name" 
                                type="text" 
                                get={name} 
                                set={setName} 
                                required={true}
                                className="w-full"
                            />
                            <p className="text-xs font-medium text-gray-400 mt-3">{t('sub_vendors.create.name_description')}</p>
                        </div>

                        <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-50 hover:shadow-md transition-all">
                            <TextInput
                                name="sharePercentage"
                                label={t('sub_vendors.create.share_percentage')}
                                id="sharePercentage"
                                type="number"
                                get={sharePercentage}
                                set={setSharePercentage}
                                required={true}
                                className="w-full"
                            />
                            <p className="text-xs font-medium text-gray-400 mt-3">
                                {t('sub_vendors.create.share_percentage_description')}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white/60 backdrop-blur-md rounded-[2.5rem] shadow-sm border border-gray-100 mb-8 overflow-hidden">
                <div className="px-8 py-5 border-b border-gray-100/50">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-2xl bg-cyan-50 text-[#0b6a8c]">
                            <BanknotesIcon className="h-5 w-5" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">{t('sub_vendors.create.supplier_details')}</h3>
                    </div>
                </div>
                <div className="p-8">
                    <div className="bg-yellow-50/50 text-yellow-700 p-5 rounded-3xl border border-yellow-100/50 mb-8 font-medium text-sm">
                        <p>
                            {t('sub_vendors.create.supplier_details_description')}
                        </p>
                    </div>

                    <div className="mb-8 p-6 bg-white/40 rounded-3xl border border-gray-50">
                        <Toggle get={displaySupplierDetails} set={setDisplaySupplierDetails}>
                            <span className="font-bold text-gray-900">{t('sub_vendors.create.display_supplier_details')}</span>
                        </Toggle>
                        <p className="text-xs font-medium text-gray-400 mt-3 ml-12">
                            {t('sub_vendors.create.display_supplier_details_description')}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-50 hover:shadow-md transition-all">
                            <TextInput 
                                name="businessName" 
                                label={t('sub_vendors.table.businessName')} 
                                id="businessName" 
                                type="text"
                                get={businessName} 
                                set={setBusinessName} 
                                required={true}
                                className="w-full"
                            />
                            <p className="text-xs font-medium text-gray-400 mt-3">{t('sub_vendors.create.business_name_example')}</p>
                        </div>

                        <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-50 hover:shadow-md transition-all">
                            <TextInput 
                                name="email" 
                                label={t('sub_vendors.table.email')} 
                                id="email" 
                                type="text" 
                                get={email} 
                                set={setEmail}
                                required={true}
                                className="w-full"
                            />
                            <p className="text-xs font-medium text-gray-400 mt-3">{t('sub_vendors.create.email_example')}</p>
                        </div>

                        <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-50 hover:shadow-md transition-all">
                            <TextInput 
                                name="phone" 
                                label={t('sub_vendors.table.phone')} 
                                id="phone" 
                                type="text" 
                                get={phone} 
                                set={setPhone}
                                required={true}
                                className="w-full"
                            />
                            <p className="text-xs font-medium text-gray-400 mt-3">{t('sub_vendors.create.phone_example')}</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white/60 backdrop-blur-md rounded-[2.5rem] shadow-sm border border-gray-100 mb-8 overflow-hidden">
                <div className="px-8 py-5 border-b border-gray-100/50">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-2xl bg-cyan-50 text-[#0b6a8c]">
                            <BuildingLibraryIcon className="h-5 w-5" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">{t('sub_vendors.create.bank_details')}</h3>
                    </div>
                </div>
                <div className="p-8">
                    <div className="bg-cyan-50/50 text-[#0b6a8c] p-5 rounded-3xl border border-cyan-100/50 mb-8 font-medium text-sm">
                        <p>
                            {t('sub_vendors.create.bank_details_description')}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-50 hover:shadow-md transition-all">
                            <TextInput 
                                name="bankAccountHolderName" 
                                label={t('sub_vendors.create.bank_account_holder_name')} 
                                id="bankAccountHolderName" 
                                type="text" 
                                get={bankAccountHolderName} 
                                set={setBankAccountHolderName}
                                className="w-full"
                            />
                            <p className="text-xs font-medium text-gray-400 mt-3">{t('sub_vendors.create.bank_account_holder_name_example')}</p>
                        </div>

                        <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-50 hover:shadow-md transition-all">
                            <TextInput 
                                name="bankAccount" 
                                label={t('sub_vendors.create.bank_account')} 
                                id="bankAccount" 
                                type="text" 
                                get={bankAccount} 
                                set={setBankAccount}
                                className="w-full"
                            />
                            <p className="text-xs font-medium text-gray-400 mt-3">{t('sub_vendors.create.bank_account_example')}</p>
                        </div>

                        <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-50 hover:shadow-md transition-all">
                            <TextInput 
                                name="iban" 
                                label={t('sub_vendors.create.iban')} 
                                id="iban" 
                                type="text" 
                                get={iban} 
                                set={setIban}
                                className="w-full"
                            />
                            <p className="text-xs font-medium text-gray-400 mt-3">{t('sub_vendors.create.iban_example')}</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-4">
                <Button 
                    className="rounded-2xl px-10 shadow-lg shadow-blue-600/20"
                    onClick={handleSubVendorForm}
                >
                    {t('sub_vendors.create.create_button')}
                </Button>
                <Button 
                    secondary={true}
                    className="rounded-2xl px-8"
                    onClick={() => navigate('/merchant/sub-vendors')}
                >
                    {t('sub_vendors.create.cancel_button')}
                </Button>
            </div>
        </DashboardLayout>
    );
}

export default SubVendorCreate;
